import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_MAP_STATE, MAP_OVERLAYS } from "./constants";
import { IMapCoordinates, IMapsetChange, IMapState, IProviderChange } from "./interfaces";
import { CONFIG } from "~/config";

const mapSlice = createSlice({
	name: "map",
	initialState: DEFAULT_MAP_STATE,
	reducers: {
		setCoordinates: (state: IMapState, action: PayloadAction<Partial<IMapCoordinates>>) => {
			state.longitude = action.payload.longitude || state.longitude;
			state.latitude = action.payload.latitude || state.latitude;
			state.zoom = action.payload.zoom || state.zoom;
			state.yaw = action.payload.yaw || state.yaw;
			state.pitch = action.payload.pitch || state.pitch;
			state.height = action.payload.height || state.height;
			state.animate = "animate" in action.payload ? action.payload.animate : false;
			state.ignore = "ignore" in action.payload ? action.payload.ignore : false;
			state.animating = "animating" in action.payload ? action.payload.animating : state.animating;

			if (state.animate) {
				state.animating = true;
			}
		},
		setOverlays: (state: IMapState, action: PayloadAction<Pick<IMapState, "overlays">>) => {
			state.overlays = action.payload.overlays;
		},
		setOphotoTimeline: (state: IMapState, action: PayloadAction<string>) => {
			state.ophotoTimeline = action.payload;
		},
		setProvider: (state: IMapState, action: PayloadAction<Partial<IProviderChange>>) => {
			const { provider, providerChanging } = action.payload;

			if ("provider" in action.payload) {
				state.provider = provider;
			}

			if ("providerChanging" in action.payload) {
				state.providerChanging = providerChanging;

				// pokud prepiname z 3D, zapominame yaw, pitch a height, aby se do toho priste zase pekne animovalo
				if (state.provider !== "m3d") {
					state.yaw = 0;
					state.pitch = 0;
					state.height = 0;
				}
			}
		},
		setMapset: (state: IMapState, action: PayloadAction<Partial<IMapsetChange>>) => {
			let { mapset } = "mapset" in action.payload ? action.payload : state;

			// prepiname do 3D nebo v nem uz jsme, takze je treba prevest neexistujici na existujici
			if (state.provider === "m3d" && ["turisticka", "letecka", "zimni"].indexOf(mapset) === -1) {
				mapset = "turisticka";
			}

			if (!state.mapset || state.mapset !== mapset || state.provider === "m3d") {
				let mapsetConfig = null;

				for (let index = 0; index < CONFIG.mapsets.length; index++) {
					if (CONFIG.mapsets[index].seo && CONFIG.mapsets[index].seo === state.mapset) {
						mapsetConfig = CONFIG.mapsets[index];
						break;
					}
				}

				if (mapsetConfig) {
					state.mapset = mapset;
				} else {
					mapsetConfig = CONFIG.mapsets[0];
					state.mapset = mapsetConfig.seo;
				}
			}

			// TODO: validovat a zaridit spravne nastaveni exkluzivit a tak dale
			// TODO: overlays mozna uz nebude treba a na praci bude stacit currentOverlays
			if (action.payload.overlays) {
				state.overlays = Array.from(action.payload.overlays);
				state.currentOverlays[state.mapset] = Array.from(action.payload.overlays);
			} else if (state.mapset in state.currentOverlays) {
				state.overlays = state.currentOverlays[state.mapset];
			} else if (state.mapset === "zimni") {
				state.overlays = [MAP_OVERLAYS.SKI];
				state.currentOverlays[state.mapset] = [MAP_OVERLAYS.SKI];
			} else {
				state.overlays = [];
				state.currentOverlays[state.mapset] = [];
			}
		},
		setOphotoDate: (state: IMapState, action: PayloadAction<string>) => {
			state.ophotoDate = action.payload;
		},
		setCopyrights: (state: IMapState, action: PayloadAction<Array<string>>) => {
			state.copyrights = action.payload;
		},
		setEgg: (state: IMapState, action: PayloadAction<IMapState["egg"]>) => {
			state.egg = action.payload;
		},
		setFit: (state: IMapState, action: PayloadAction<IMapState["fit"]>) => {
			if (!action.payload.length) {
				state.fit = [];

				return;
			}

			// bez ohledu na to, kolik jsme dostali souradnic, spocteme proste jen bounding box, tedy dve souradnice
			let minLatitude = Infinity;
			let minLongitude = Infinity;
			let maxLatitude = -Infinity;
			let maxLongitude = -Infinity;

			for (let index = 0, len = action.payload.length; index < len; index++) {
				const coords = action.payload[index];

				minLongitude = Math.min(coords[0], minLongitude);
				maxLongitude = Math.max(coords[0], maxLongitude);
				minLatitude = Math.min(coords[1], minLatitude);
				maxLatitude = Math.max(coords[1], maxLatitude);
			}

			state.fit = [[minLongitude, minLatitude], [maxLongitude, maxLatitude]];
		},
		setDesaturate: (state: IMapState, action: PayloadAction<boolean>) => {
			state.desaturate = action.payload;
		},
	},
});

// dle "duck" metodiky jsou akce pojmenovane exporty a reducer default export
export const { setCoordinates, setOverlays, setOphotoTimeline, setMapset, setProvider, setOphotoDate, setCopyrights, setEgg, setFit, setDesaturate } = mapSlice.actions;
export default mapSlice.reducer;
